'use client';

// imports
import Link from 'next/link';
import { Scan, Info, ArrowRight, Activity, Shield, Zap } from 'lucide-react';
import LightWavesBackground from '@/components/ui/LightWavesBackground';
import LogoLoop from '@/components/ui/LogoLoop';
import TextType from '@/components/ui/TextType';
import { useInView } from '@/hooks/useInView';

// constants
const logos = [
  { src: '/assets/images/logo_loop/dost.png', alt: 'DOST-SEI Logo' },
  { src: '/assets/images/logo_loop/usep.png', alt: 'USeP Logo' },
  { src: '/assets/images/logo_loop/cic.png', alt: 'CIC Logo' },
];

const base = 'transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]';
const hidden = 'opacity-0 translate-y-8';
const show = 'opacity-100 translate-y-0';

// component
export default function HeroSection() {

  // hooks
  const { ref, isVisible } = useInView<HTMLDivElement>({
    threshold: 0.1,
    once: true,
  });

  return (
    <section ref={ref} className="relative overflow-hidden min-h-[95vh] flex items-center bg-gradient-to-b from-blue-50 to-white">
  
      {/* bg */}
      <LightWavesBackground />

      {/* overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-white/10 to-transparent pointer-events-none z-[1]" />
      
      {/* grid */}
      <div className="absolute inset-0 opacity-[0.025] pointer-events-none z-[1]"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, #3b82f6 1px, transparent 0)`,
          backgroundSize: '40px 40px',
        }}
      />

      {/* container */}
      <div className="relative z-10 max-w-screen-xl mx-auto px-6 lg:px-10 xl:px-16 pt-20 py-0 w-full">
        <div className="flex flex-col items-center gap-12 lg:gap-16">
          <div className="w-full max-w-5xl text-center">
            <div className={`${base} ${isVisible ? show : hidden} mb-4 md:mb-6 min-h-[24px] md:min-h-[28px]`}>
              <TextType
                text="Welcome to Insighta AI!"
                typingSpeed={70}
                deletingSpeed={40}
                pauseDuration={3000}
                showCursor={true}
                cursorCharacter="|"
                className="text-sm md:text-base font-medium tracking-tight text-blue-500/70"
              />
            </div>

            {/*title*/}
            <h1 className={`${base} delay-100 ${isVisible ? show : hidden} text-3xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6`}>
              AI-Powered{' '}
            <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent animate-aurora-text">
              Diabetes Insights
            </span>{' '}
              from Tongue Images
            </h1>

            {/*description*/}
            <p className={`${base} delay-200 ${isVisible ? show : hidden} text-slate-600 text-sm md:text-base lg:text-base leading-relaxed mb-8 max-w-xl md:max-w-xl lg:max-w-3xl mx-auto`}>
              Insighta AI analyzes tongue images to assess your diabetes risk in seconds. 
              Non-invasive, instant, and understandable — accessible diabetes risk screening for everyone.
            </p>

            {/* buttons */}
            <div className={`${base} delay-300 ${isVisible ? show : hidden} flex flex-col sm:flex-row items-center justify-center gap-4 mb-10`}>
              <Link
                href="/upload-page"
                className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 shadow-blue-500/25 text-white font-semibold rounded-full px-8 py-4 text-base shadow-blue hover:shadow-blue-strong hover:from-blue-600 hover:to-blue-700 hover:scale-[1.02] transition-all duration-200 active:scale-95 group w-full sm:w-auto justify-center"
              >
                <Scan size={18} />
                Start Scan
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-200" />
              </Link>
              <Link
                href="/about-page"
                className="flex items-center gap-2 bg-white text-blue-600 font-semibold rounded-full px-8 py-4 text-base border border-blue-200 shadow-sm hover:bg-blue-50 hover:border-blue-300 transition-all duration-200 active:scale-95 w-full sm:w-auto justify-center"
              >
                <Info size={18} />
                About Us
              </Link>
            </div>

            {/* trust indicators */}
            <div className={`${base} delay-400 ${isVisible ? show : hidden} flex flex-wrap items-center justify-center gap-6 text-xs md:text-sm lg:text-sm text-slate-500`}>
              <div className="flex items-center gap-1.5">
                <Shield size={14} className="text-emerald-500" />
                <span>No account required</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Zap size={14} className="text-amber-500" />
                <span>Results in seconds</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Activity size={14} className="text-blue-500" />
                <span>Explainable AI</span>
              </div>
            </div>

            {/* logo Loop */}
            <div className= {`${base} delay-500 ${isVisible ? show : hidden} mt-20 mb-10 w-full max-w-xl mx-auto relative `}>
                {/* <p className="text-xs md:text-sm text-slate-200 tracking-wide mb-4">
                  ACKNOWLEDGEMENT
                </p> */}

              <div className="logo-fade-mask ">
                <LogoLoop
                  logos={logos}
                  speed={35}
                  gap={50}
                  logoHeight={50}
                  pauseOnHover
                  scaleOnHover
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      
    </section>
  );
}